import api from './api';
import { API_BASE, MEDIA_BASE } from './config';

export type MediaFolder = {
    folder_id: number;
    folder_title: string;
    assets_count: number;
    cover_url?: string | null;
    first_added_at?: string | null;
    last_added_at?: string | null;
};

export type MediaAsset = {
    id: number;
    source: 'media' | 'audio';
    owner_user_id: number | null;
    content_id: number;
    kind: 'image' | 'audio' | 'video' | 'doc' | 'other';
    storage_type: 'UPLOAD' | 'LINK';
    file_name: string;
    file_path?: string | null;
    file_url?: string | null;
    mime_type?: string | null;
    byte_size?: number | null;
    width?: number | null;
    height?: number | null;
    duration_seconds?: number | null;
    description?: string | null;
    created_at: string;
    updated_at: string;
    url?: string | null;
};

export function resolveUrl(u?: string | null) {
    if (!u) return null;
    if (/^https?:\/\//i.test(u)) return u;
    const clean = u.replace(/^\/+/, '');
    const base = clean.startsWith('media/') ? MEDIA_BASE : API_BASE;
    return `${base}/${clean}`;
}

/* FOLDERS */
export async function listMediaFolders(params?: {
    q?: string;
    includeEmpty?: boolean;
    limit?: number;
    offset?: number;
}): Promise<{ items: MediaFolder[]; limit: number; offset: number }> {
    // ważne: ścieżka z prefiksem /api
    const res = await api.get('/media/folders', { params });
    return res.data;
}

/* ASSETS IN FOLDER */
export async function getFolderAssets(
    contentId: number,
    params?: { q?: string; kind?: string; limit?: number; offset?: number }
): Promise<{ items: MediaAsset[]; limit: number; offset: number }> {
    const res = await api.get(`/media/folders/${contentId}/assets`, { params });
    const data = res.data;
    data.items = (data.items || []).map((a: MediaAsset) => ({
        ...a,
        url: resolveUrl(a.url || a.file_url || null),
    }));
    return data;
}

/* INFO PER FOLDER */
export async function getFolderInfo(
    contentId: number
): Promise<{ content_id: number; total: number; by_kind: Record<string, number> }> {
    const res = await api.get(`/media/folders/${contentId}/info`);
    return res.data;
}

/* UPLOAD */
export async function uploadAsset(contentId: number, file: File): Promise<MediaAsset> {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post(`/media/folders/${contentId}/assets/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    const a: MediaAsset = res.data;
    a.url = resolveUrl(a.url || a.file_url || null);
    return a;
}

/* LINK */
export async function linkAsset(
    contentId: number,
    payload: { url: string; file_name?: string; kind?: string; description?: string }
): Promise<MediaAsset> {
    const res = await api.post(`/media/folders/${contentId}/assets/link`, payload);
    const a: MediaAsset = res.data;
    a.url = resolveUrl(a.url || a.file_url || null);
    return a;
}

/* UPDATE (media only) */
export async function updateAsset(
    id: number,
    payload: Partial<Pick<MediaAsset, 'file_name' | 'description' | 'kind' | 'content_id'>>
): Promise<MediaAsset> {
    const res = await api.patch(`/media/assets/${id}`, payload);
    const a: MediaAsset = res.data;
    a.url = resolveUrl(a.url || a.file_url || null);
    return a;
}

/* DELETE (media) */
export async function deleteAsset(id: number): Promise<{ ok: boolean }> {
    const res = await api.delete(`/media/assets/${id}`);
    return res.data;
}

/* DELETE (audio) */
export async function deleteAudio(id: number): Promise<{ ok: boolean }> {
    const res = await api.delete(`/media/audios/${id}`);
    return res.data;
}
